import type { SwitchboardFlowData } from '../converters/shared/reactFlowTypes'
import {
  getSavedSwitchboardSnapshotFromStorage,
  type SavedSwitchboardSnapshot,
} from './switchboardSavedSnapshot'

/**
 * Which source of truth owns the editor canvas at mount.
 *
 * Precedence (highest first):
 *   1. `draft` — an unsaved local draft that is strictly newer than the saved
 *      snapshot. A real post-save edit writes a draft (with a fresh `lastSaved`)
 *      without clearing the snapshot, so a draft newer than the snapshot must
 *      win; otherwise the stale snapshot would hide the user's in-progress work.
 *   2. `saved-snapshot` — a short-lived post-save pin, used only when it is for
 *      this switchboard, not expired, NOT older than the current server
 *      version, and not superseded by a newer draft. This protects the
 *      read-after-write window where a stale GET can still return the pre-save
 *      value.
 *   3. `draft` — an unsaved local draft for this switchboard (when no snapshot
 *      applies). It intentionally owns the editor so the user's in-progress
 *      edits are not discarded. The snapshot `updated_at` gate does NOT apply to
 *      drafts.
 *   4. `server` — the authoritative persisted flow data.
 */
export type InitialFlowOwner = 'saved-snapshot' | 'draft' | 'server'

export interface InitialFlowOwnership {
  initialFlowData: SwitchboardFlowData
  hadDraftOnMount: boolean
  savedSnapshotOnMount: SavedSwitchboardSnapshot | null
  owner: InitialFlowOwner
}

/** A draft plus the metadata needed to compare it against a saved snapshot. */
export interface DraftWithMeta {
  flowData: SwitchboardFlowData
  /** Local wall-clock time (ms) when the draft was last written. */
  lastSaved?: number
}

export interface ResolveInitialFlowOwnershipParams {
  extensionDialPlan: number
  /** Deserialized server flow data (the fallback / authoritative source). */
  serverFlowData: SwitchboardFlowData
  /** The server record's `updated_at`, used only to discard an older saved pin. */
  serverUpdatedAt?: string
  /**
   * Reads a saved post-save snapshot from storage, already gated on
   * `serverUpdatedAt`. Injectable for testing.
   */
  readSavedSnapshot?: (
    switchboardId: number,
    serverUpdatedAt?: string,
  ) => SavedSwitchboardSnapshot | null
  /** Reads an unsaved local draft from storage. Injectable for testing. */
  readDraft: (switchboardId: number) => SwitchboardFlowData | null
  /**
   * Optional reader that also returns draft metadata (`lastSaved`). When
   * provided, a draft newer than the saved snapshot wins over the snapshot.
   * Injectable for testing.
   */
  readDraftMeta?: (switchboardId: number) => DraftWithMeta | null
}

/**
 * Pure resolution of who owns the editor canvas on mount. Extracted from
 * `SwitchboardEditPage` so the precedence rules (and the `updated_at`-gated
 * saved-snapshot behavior that fixes the "saved value reverts after navigating
 * away and back" bug) can be tested exhaustively without rendering the page.
 */
export function resolveInitialFlowOwnership({
  extensionDialPlan,
  serverFlowData,
  serverUpdatedAt,
  readSavedSnapshot = getSavedSwitchboardSnapshotFromStorage,
  readDraft,
  readDraftMeta,
}: ResolveInitialFlowOwnershipParams): InitialFlowOwnership {
  const savedSnapshot = readSavedSnapshot(extensionDialPlan, serverUpdatedAt)

  const draftMeta = readDraftMeta?.(extensionDialPlan) ?? null

  if (savedSnapshot) {
    // A draft written strictly after the snapshot represents newer, unsaved
    // user edits (e.g. edits made during the post-save read-lag window). Let it
    // win so those edits are not hidden by the stale snapshot.
    if (
      draftMeta &&
      typeof draftMeta.lastSaved === 'number' &&
      Number.isFinite(draftMeta.lastSaved) &&
      draftMeta.lastSaved > savedSnapshot.savedAt
    ) {
      return {
        initialFlowData: draftMeta.flowData,
        hadDraftOnMount: true,
        savedSnapshotOnMount: null,
        owner: 'draft',
      }
    }

    return {
      initialFlowData: savedSnapshot.flowData,
      hadDraftOnMount: false,
      savedSnapshotOnMount: savedSnapshot,
      owner: 'saved-snapshot',
    }
  }

  const existingDraft = draftMeta?.flowData ?? readDraft(extensionDialPlan)
  if (existingDraft) {
    return {
      initialFlowData: existingDraft,
      hadDraftOnMount: true,
      savedSnapshotOnMount: null,
      owner: 'draft',
    }
  }

  return {
    initialFlowData: serverFlowData,
    hadDraftOnMount: false,
    savedSnapshotOnMount: null,
    owner: 'server',
  }
}
